import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import Box from '@mui/material/Box';
import MuiButton from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import React from 'react';

import { canBulkUpdateJobs } from './bulkPermissions';
import { ActionPermissions, CustomPermission } from '../../@types';
import { Link } from '../../components/atoms/Link';
import { isUserHasPermission } from '../../configs/permissions';
import { STANDARD_FIELD_HEIGHT } from '../../configs/ui-constants';

export interface ServiceListHeaderProps {
  total: number;
  permissions: CustomPermission[] | undefined;
  exportAnchor: HTMLElement | null;
  onExportClick: (event: React.MouseEvent<HTMLElement>) => void;
  onExportClose: () => void;
  onExportCsv: () => void;
  onExportExcel: () => void;
  onRecentJobsClick: (event: React.MouseEvent<HTMLElement>) => void;
  /** Callback to open the column-picker dialog. When omitted, the Columns
   *  button is hidden — used for the client/customer curated view. */
  onColumnsClick?: () => void;
  /** Callback to open the saved-reports modal. */
  onReportsClick: () => void;
  /** Opens Bulk Edit with no selection, where an uploaded file names the jobs.
   *  When omitted, the button is hidden — used for the client/customer view. */
  onBulkUpdateClick?: () => void;
}

// Outlined buttons sitting on the dark sidebar-coloured toolbar — use
// translucent white border + text so they stay legible on the dark ground.
const TOOLBAR_BUTTON_SX = {
  textTransform: 'none' as const,
  fontSize: '12px',
  color: '#fff',
  borderColor: 'rgba(255, 255, 255, 0.3)',
  bgcolor: 'transparent',
  py: 0.25,
  height: STANDARD_FIELD_HEIGHT,
  minHeight: STANDARD_FIELD_HEIGHT,
  '&:hover': {
    borderColor: '#fff',
    bgcolor: 'rgba(255, 255, 255, 0.08)',
  },
} as const;

export const ServiceListHeader: React.FC<ServiceListHeaderProps> = ({
  total,
  permissions,
  exportAnchor,
  onExportClick,
  onExportClose,
  onExportCsv,
  onExportExcel,
  onRecentJobsClick,
  onColumnsClick,
  onReportsClick,
  onBulkUpdateClick,
}) => {
  return (
    <Box
      component="header"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: 'v4.sidebar',
        color: '#fff',
        px: '20px',
        py: '12px',
        flexWrap: 'wrap',
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>Jobs</Typography>
        {total > 0 && (
          <Box
            component="span"
            sx={{
              fontSize: '11px',
              color: 'rgba(255, 255, 255, 0.85)',
              fontWeight: 400,
              bgcolor: 'rgba(255, 255, 255, 0.12)',
              px: 1,
              py: '2px',
              borderRadius: '10px',
            }}
          >
            {total.toLocaleString()} results
          </Box>
        )}
        <IconButton
          size="small"
          aria-label="Recent jobs"
          onClick={onRecentJobsClick}
          sx={{ color: 'rgba(255, 255, 255, 0.85)', '&:hover': { color: '#fff' } }}
        >
          <AccessTimeIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
      <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
        {isUserHasPermission(ActionPermissions.Report_My_Report_Search, permissions) && (
          <MuiButton
            variant="outlined"
            size="small"
            startIcon={<SummarizeOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={onReportsClick}
            sx={TOOLBAR_BUTTON_SX}
          >
            Reports
          </MuiButton>
        )}
        {onColumnsClick && (
          <MuiButton
            variant="outlined"
            size="small"
            startIcon={<ViewColumnOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={onColumnsClick}
            sx={TOOLBAR_BUTTON_SX}
          >
            Columns
          </MuiButton>
        )}
        {onBulkUpdateClick && canBulkUpdateJobs(permissions) && (
          <MuiButton
            variant="outlined"
            size="small"
            startIcon={<EditOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={onBulkUpdateClick}
            sx={TOOLBAR_BUTTON_SX}
          >
            Bulk Update
          </MuiButton>
        )}
        {isUserHasPermission(ActionPermissions.Service_Export, permissions) && (
          <>
            <MuiButton
              variant="outlined"
              size="small"
              startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 14 }} />}
              onClick={onExportClick}
              sx={TOOLBAR_BUTTON_SX}
            >
              Export
            </MuiButton>
            <Menu
              anchorEl={exportAnchor}
              open={Boolean(exportAnchor)}
              onClose={onExportClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={onExportCsv}>Export CSV</MenuItem>
              <MenuItem onClick={onExportExcel}>Export Excel</MenuItem>
            </Menu>
          </>
        )}
        {isUserHasPermission(ActionPermissions.Service_Create, permissions) && (
          <Link href="/services/create">
            <MuiButton
              variant="outlined"
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 14 }} />}
              sx={TOOLBAR_BUTTON_SX}
            >
              New Job
            </MuiButton>
          </Link>
        )}
      </Box>
    </Box>
  );
};
